import { apiFetch } from '@/lib/apiFetch';
import type {
  CreateOfferingRequest,
  OfferingResponse,
  OfferingsListResponse,
  UpsertPricingPlanRequest,
  PricingPlanView,
  TeachingModeDefaultResponse,
} from '@/types/offering';

// ─── Helper ──────────────────────────────────────────────────────────────────

async function authFetch<T>(path: string, accessToken: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(path, {
    ...options,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      ...options?.headers,
    },
  });
  if (response.status === 401) throw new Error('Authentication failed. Please log in again.');
  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    const text = await response.text();
    let msg = text;
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json.errors)) msg = json.errors.join(', ');
      else msg = json.message || text;
    } catch {
      // text was not JSON
    }
    throw new Error(msg || `Request failed: ${response.status}`);
  }
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

// ─── Offering CRUD ───────────────────────────────────────────────────────────

export const createOffering = async (
  data: CreateOfferingRequest,
  accessToken: string,
): Promise<OfferingResponse> =>
  authFetch('/api/v1/offerings', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const getOfferingById = async (id: string, accessToken: string): Promise<OfferingResponse | null> => {
  const response = await apiFetch(`/api/v1/offerings/${id}`, {
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) throw new Error('Authentication failed. Please log in again.');
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('Failed to fetch offering');
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export const getOfferings = async (accessToken: string): Promise<OfferingsListResponse> =>
  authFetch('/api/v1/offerings', accessToken, { method: 'GET' });

export const updateOffering = async (
  id: string,
  data: Partial<CreateOfferingRequest>,
  accessToken: string,
): Promise<OfferingResponse> =>
  authFetch(`/api/v1/offerings/${id}`, accessToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const deleteTeacherOfferings = async (accessToken: string, id: string): Promise<void> =>
  authFetch(`/api/v1/offerings/${id}`, accessToken, { method: 'DELETE' });

// ─── Publish / Unpublish ─────────────────────────────────────────────────────

export const publishOfferingById = async (id: string, accessToken: string): Promise<OfferingResponse> =>
  authFetch(`/api/v1/offerings/${id}/publish`, accessToken, { method: 'POST' });

export const unpublishOfferingById = async (id: string, accessToken: string): Promise<OfferingResponse> =>
  authFetch(`/api/v1/offerings/${id}/unpublish`, accessToken, { method: 'POST' });

// ─── Pricing plans ────────────────────────────────────────────────────────────

export const upsertPricingPlan = async (
  offeringId: string,
  currency: string,
  data: UpsertPricingPlanRequest,
  accessToken: string,
): Promise<PricingPlanView> =>
  authFetch(`/api/v1/offerings/${offeringId}/pricing-plans/${currency}`, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const deletePricingPlan = async (offeringId: string, currency: string, accessToken: string): Promise<void> =>
  authFetch(`/api/v1/offerings/${offeringId}/pricing-plans/${currency}`, accessToken, { method: 'DELETE' });

// ─── Teaching mode default ────────────────────────────────────────────────────

export const getTeachingModeDefault = async (accessToken: string): Promise<TeachingModeDefaultResponse> =>
  authFetch('/api/v1/offerings/teaching-mode-default', accessToken, { method: 'GET' });

// ─── Legacy aliases ───────────────────────────────────────────────────────────
// Keep old function name so pages that haven't been migrated still compile.

/** @deprecated Use createOffering */
export const publishOffering = async (offeringData: any, accessToken: string): Promise<any> =>
  createOffering(offeringData, accessToken);
